import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tooltip,
	Typography,
} from '@mui/material';

import { useApiClient, useToastService } from '@@common';

import { ContextClient, ContextModel, ServiceConnectionClient, ServiceConnectionModel, ServiceConnectionModelPagingResultModel } from '../../api/api';
import ServiceConnectionStatusChip from '../ServiceConnections/ServiceConnectionStatusChip';
import { contextDesignerQueryKeys } from './ContextDesignerQueryKeys';

type ContextServiceConnectionsViewProps = {
	contextId: string;
	initialContext?: ContextModel | null;
};

type SortBy = 'name' | 'type' | 'category' | 'linked';
type SortDirection = 'asc' | 'desc';

const contextLinkableCategory = 'Sys:ContextLinkable';

function getSortValue(serviceConnection: ServiceConnectionModel, sortBy: Exclude<SortBy, 'linked'>): string {
	if (sortBy === 'name') {
		return serviceConnection.name ?? '';
	}

	if (sortBy === 'type') {
		return serviceConnection.type ?? '';
	}

	return serviceConnection.category ?? '';
}

export default function ContextServiceConnectionsView({ contextId, initialContext }: ContextServiceConnectionsViewProps) {
	const contextClient = useApiClient(ContextClient);
	const serviceConnectionClient = useApiClient(ServiceConnectionClient);
	const queryClient = useQueryClient();
	const toast = useToastService();
	const [workingId, setWorkingId] = useState<string | null>(null);
	const [sortBy, setSortBy] = useState<SortBy>('name');
	const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

	const { data: context } = useQuery<ContextModel>({
		queryKey: contextDesignerQueryKeys.context(contextId),
		queryFn: () => contextClient.getContextById(contextId),
		initialData: initialContext ?? undefined,
		enabled: !!contextId,
	});

	const linkedServiceConnections = useMemo(() => context?.serviceConnections ?? [], [context?.serviceConnections]);
	const linkedServiceConnectionIds = useMemo(
		() => linkedServiceConnections.map((item) => item.serviceConnectionId).sort((left, right) => left.localeCompare(right)),
		[linkedServiceConnections]
	);

	const linkedServiceConnectionIdSet = useMemo(() => new Set(linkedServiceConnectionIds), [linkedServiceConnectionIds]);

	const {
		data: serviceConnectionSearch,
		isLoading: isLoadingServiceConnections,
		refetch: refetchServiceConnections,
	} = useQuery<ServiceConnectionModelPagingResultModel>({
		queryKey: contextDesignerQueryKeys.availableServiceConnections(),
		queryFn: () => serviceConnectionClient.search({ paging: { pageIndex: 0, pageSize: 1000, sortAscending: true } }),
	});

	const {
		data: serviceConnectionMetadata,
		isLoading: isLoadingServiceConnectionMetadata,
		refetch: refetchServiceConnectionMetadata,
	} = useQuery({
		queryKey: contextDesignerQueryKeys.serviceConnectionMetadata(),
		queryFn: () => serviceConnectionClient.getMetadata(),
	});

	const isLoading = isLoadingServiceConnections || isLoadingServiceConnectionMetadata;

	const contextLinkableServiceConnectionTypes = useMemo(() => {
		return new Set((serviceConnectionMetadata ?? []).filter((item) => item.categories?.includes(contextLinkableCategory)).map((item) => item.name));
	}, [serviceConnectionMetadata]);

	const serviceConnections = useMemo(() => {
		const sortMultiplier = sortDirection === 'asc' ? 1 : -1;

		return (serviceConnectionSearch?.items ?? [])
			.filter((item) => contextLinkableServiceConnectionTypes.has(item.type))
			.sort((left, right) => {
				let result = 0;

				if (sortBy === 'linked') {
					result = Number(linkedServiceConnectionIdSet.has(left.id)) - Number(linkedServiceConnectionIdSet.has(right.id));
				} else {
					result = getSortValue(left, sortBy).localeCompare(getSortValue(right, sortBy), undefined, { sensitivity: 'base' });
				}

				return result === 0 ? left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }) : result * sortMultiplier;
			});
	}, [contextLinkableServiceConnectionTypes, linkedServiceConnectionIdSet, serviceConnectionSearch?.items, sortBy, sortDirection]);

	const handleSort = (nextSortBy: SortBy) => {
		if (sortBy === nextSortBy) {
			setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
			return;
		}

		setSortBy(nextSortBy);
		setSortDirection('asc');
	};

	const updateContextCache = (updatedContext: ContextModel) => {
		queryClient.setQueryData(contextDesignerQueryKeys.context(contextId), updatedContext);
		queryClient.setQueryData(contextDesignerQueryKeys.designerSnapshot(contextId, true), (snapshot: any) =>
			snapshot ? { ...snapshot, context: updatedContext } : snapshot,
		);
	};

	const handleEnable = async (serviceConnection: ServiceConnectionModel) => {
		setWorkingId(serviceConnection.id);
		try {
			const updatedContext = await contextClient.upsertServiceConnection(contextId, serviceConnection.id, {
				serviceConnectionId: serviceConnection.id,
				serviceConnectionType: serviceConnection.type,
			});
			updateContextCache(updatedContext);
			toast.success(`Enabled ${serviceConnection.name}`);
		} catch (error) {
			toast.error(`Failed to enable service connection: ${error instanceof Error ? error.message : 'Unknown error'}`);
		} finally {
			setWorkingId(null);
		}
	};

	const handleDisable = async (serviceConnection: ServiceConnectionModel) => {
		setWorkingId(serviceConnection.id);
		try {
			const updatedContext = await contextClient.removeServiceConnection(contextId, serviceConnection.id);
			updateContextCache(updatedContext);
			toast.success(`Disabled ${serviceConnection.name}`);
		} catch (error) {
			toast.error(`Failed to disable service connection: ${error instanceof Error ? error.message : 'Unknown error'}`);
		} finally {
			setWorkingId(null);
		}
	};

	const handleToggle = async (serviceConnection: ServiceConnectionModel, enabled: boolean) => {
		if (enabled) {
			await handleEnable(serviceConnection);
		} else {
			await handleDisable(serviceConnection);
		}
	};

	return (
		<Stack direction="column" sx={{ flex: 1, minHeight: 0, overflow: 'hidden', bgcolor: 'background.default' }}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{ px: 2, py: 1.5, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
			>
				<Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
					<LinkIcon color="action" />
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="subtitle1" fontWeight={700}>
							Service Connectors
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{linkedServiceConnections.length} linked to this context
						</Typography>
					</Box>
				</Stack>
				<Tooltip title="Refresh service connectors">
					<span>
						<IconButton
							size="small"
							onClick={() => {
								refetchServiceConnections();
								refetchServiceConnectionMetadata();
							}}
							disabled={isLoading}
						>
							<RefreshIcon />
						</IconButton>
					</span>
				</Tooltip>
			</Stack>

			<Alert
				severity="info"
				variant="standard"
				sx={{ m: 2 }}
				action={
					<Button component={RouterLink} to="/service-connections" color="inherit" size="small">
						Manage
					</Button>
				}
			>
				Linking a service connection to this context allows the coding agent to use advanced features provided by that service connector.
			</Alert>

			{isLoading ? (
				<Stack alignItems="center" justifyContent="center" spacing={2} sx={{ flex: 1, p: 3 }}>
					<CircularProgress size={28} />
					<Typography variant="body2" color="text.secondary">
						Loading service connectors
					</Typography>
				</Stack>
			) : serviceConnections.length === 0 ? (
				<Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ flex: 1, p: 3, textAlign: 'center' }}>
					<LinkIcon color="disabled" sx={{ fontSize: 48 }} />
					<Typography variant="subtitle2">No context-linkable service connectors configured</Typography>
					<Typography variant="body2" color="text.secondary">
						Create or configure service connections that support context linking before enabling them for this context.
					</Typography>
					<Button component={RouterLink} to="/service-connections" variant="outlined" size="small">
						Manage service connections
					</Button>
				</Stack>
			) : (
				<Stack sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
					<TableContainer sx={{ overflow: 'auto', bgcolor: 'background.paper' }}>
						<Table stickyHeader size="small" aria-label="Service connectors">
							<TableHead>
								<TableRow>
									<TableCell>
										<TableSortLabel active={sortBy === 'name'} direction={sortBy === 'name' ? sortDirection : 'asc'} onClick={() => handleSort('name')}>
											Name
										</TableSortLabel>
									</TableCell>
									<TableCell>
										<TableSortLabel active={sortBy === 'type'} direction={sortBy === 'type' ? sortDirection : 'asc'} onClick={() => handleSort('type')}>
											Type
										</TableSortLabel>
									</TableCell>
									<TableCell>
										<TableSortLabel
											active={sortBy === 'category'}
											direction={sortBy === 'category' ? sortDirection : 'asc'}
											onClick={() => handleSort('category')}
										>
											Category
										</TableSortLabel>
									</TableCell>
									<TableCell>Status</TableCell>
									<TableCell align="right">
										<TableSortLabel active={sortBy === 'linked'} direction={sortBy === 'linked' ? sortDirection : 'asc'} onClick={() => handleSort('linked')}>
											Linked
										</TableSortLabel>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{serviceConnections.map((serviceConnection) => {
									const isLinked = linkedServiceConnectionIdSet.has(serviceConnection.id);
									const isWorking = workingId === serviceConnection.id;

									return (
										<TableRow key={serviceConnection.id} hover selected={isLinked}>
											<TableCell>
												<Typography variant="body2" fontWeight={600}>
													{serviceConnection.name}
												</Typography>
											</TableCell>
											<TableCell>
												<Chip label={serviceConnection.type} size="small" />
											</TableCell>
											<TableCell>
												{serviceConnection.category ? <Chip label={serviceConnection.category} size="small" variant="outlined" /> : '-'}
											</TableCell>
											<TableCell>
												<ServiceConnectionStatusChip status={serviceConnection.statusName} size="small" />
											</TableCell>
											<TableCell align="right">
												<Tooltip title={isLinked ? 'Unlink from this context' : 'Link to this context'}>
													<span>
														<Switch
															checked={isLinked}
															disabled={isWorking}
															inputProps={{ 'aria-label': `${isLinked ? 'Unlink' : 'Link'} ${serviceConnection.name}` }}
															onChange={(event) => handleToggle(serviceConnection, event.target.checked)}
														/>
													</span>
												</Tooltip>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</Stack>
			)}
		</Stack>
	);
}
